import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import hrService from '../../../services/hrService';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';




const ProfileHeader = ({ 
  employee, 
  isEditing, 
  onEdit, 
  onSave, 
  onCancel, 
  onImageUpload,
  isAddMode = false,
  saving = false,
  canEdit = true,
  validationErrors = {} // Field-specific validation errors from parent
}) => {
  const [editedData, setEditedData] = useState(employee || {});
  const [reportingManagers, setReportingManagers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingManagers, setLoadingManagers] = useState(false);
  // CRITICAL FIX: Add render key to force complete re-render
  const [renderKey, setRenderKey] = useState(0);

  // FIXED: Predefined dropdown options
  const departmentOptions = [
    { value: 'Engineering', label: 'Engineering' },
    { value: 'Sales', label: 'Sales' },
    { value: 'Marketing', label: 'Marketing' },
    { value: 'Human Resources', label: 'Human Resources' },
    { value: 'Finance', label: 'Finance' },
    { value: 'Operations', label: 'Operations' },
    { value: 'Customer Support', label: 'Customer Support' },
    { value: 'Product', label: 'Product' },
    { value: 'Design', label: 'Design' },
    { value: 'IT', label: 'IT' }
  ];

  const positionOptions = [
    { value: 'Junior Developer', label: 'Junior Developer' },
    { value: 'Software Engineer', label: 'Software Engineer' },
    { value: 'Senior Software Engineer', label: 'Senior Software Engineer' },
    { value: 'Team Lead', label: 'Team Lead' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Senior Manager', label: 'Senior Manager' },
    { value: 'Director', label: 'Director' },
    { value: 'VP', label: 'Vice President' },
    { value: 'CTO', label: 'CTO' },
    { value: 'Sales Executive', label: 'Sales Executive' },
    { value: 'Marketing Specialist', label: 'Marketing Specialist' },
    { value: 'HR Specialist', label: 'HR Specialist' },
    { value: 'Accountant', label: 'Accountant' },
    { value: 'Support Specialist', label: 'Support Specialist' }
  ];

  const designationOptions = [
    { value: 'Intern', label: 'Intern' },
    { value: 'Associate', label: 'Associate' },
    { value: 'Staff', label: 'Staff' },
    { value: 'Senior Staff', label: 'Senior Staff' },
    { value: 'Lead', label: 'Lead' },
    { value: 'Principal', label: 'Principal' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Senior Manager', label: 'Senior Manager' },
    { value: 'Director', label: 'Director' },
    { value: 'Senior Director', label: 'Senior Director' },
    { value: 'VP', label: 'Vice President' },
    { value: 'SVP', label: 'Senior Vice President' },
    { value: 'C-Level', label: 'C-Level Executive' }
  ];

  const branchOptions = [
    { value: 'Headquarters', label: 'Headquarters' },
    { value: 'Branch Office 1', label: 'Branch Office 1' },
    { value: 'Branch Office 2', label: 'Branch Office 2' },
    { value: 'Regional Office', label: 'Regional Office' },
    { value: 'Remote', label: 'Remote' }
  ];

  const workShiftOptions = [
    { value: 'day', label: 'Day Shift (9 AM - 6 PM)' },
    { value: 'night', label: 'Night Shift (9 PM - 6 AM)' },
    { value: 'rotating', label: 'Rotating Shift' },
    { value: 'flexible', label: 'Flexible Hours' }
  ];

  const employmentStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'terminated', label: 'Terminated' },
    { value: 'on_leave', label: 'On Leave' },
    { value: 'resigned', label: 'Resigned' }
  ];

  const roleOptions = [
    { value: 'employee', label: 'Employee' },
    { value: 'manager', label: 'Manager' },
    { value: 'hr', label: 'HR' },
    { value: 'admin', label: 'Admin' }
  ];

  // CRITICAL FIX: Ensure employmentStatus always has a valid default value
  useEffect(() => {
    if (editedData && (!editedData?.employmentStatus || editedData?.employmentStatus === '' || editedData?.employmentStatus === 'unknown')) {
      const validStatuses = ['active', 'inactive', 'terminated', 'on_leave', 'resigned'];
      if (!validStatuses?.includes(editedData?.employmentStatus)) {
        setEditedData(prev => ({
          ...prev,
          employmentStatus: 'active'
        }));
      }
    }
  }, [editedData?.employmentStatus]);

  // CRITICAL FIX: Force complete component re-render when edit mode changes
  useEffect(() => {
    if (employee) {
      console.log('🔄 ProfileHeader: FORCING COMPLETE RE-RENDER - isEditing:', isEditing);
      
      // Force complete state reset
      setEditedData({
        ...employee,
        dateOfJoining: employee?.dateOfJoining || employee?.joinDate || employee?.hireDate || '',
        location: employee?.location || employee?.branchName || '',
        reportingManager: employee?.reportingManager || employee?.reportingManagerId || '',
      });
      
      // CRITICAL: Increment render key to force unmount/remount of all inputs
      setRenderKey(prev => prev + 1);
      
      console.log('✅ ProfileHeader: Complete re-render triggered, new renderKey:', renderKey + 1);
    }
  }, [employee?.id, isEditing]);

  // CRITICAL FIX: Load actual reporting managers from database with UUIDs
  useEffect(() => {
    const loadReportingManagers = async () => {
      setLoadingManagers(true);
      try {
        // Call the hrService method to get actual employees who can be managers
        const managersData = await hrService?.getReportingManagers();
        
        if (managersData && Array.isArray(managersData)) {
          // Transform to dropdown format with UUID as value
          const managerOptions = managersData?.map(manager => ({
            value: manager?.id, // CRITICAL: Use UUID as value, not string
            label: `${manager?.userProfiles?.fullName || 'Unknown'} - ${manager?.employeeId || ''}`
          }));
          
          console.log('Loaded reporting managers:', managerOptions);
          setReportingManagers(managerOptions);
        } else {
          console.warn('No managers found or invalid response');
          setReportingManagers([]);
        }
      } catch (error) {
        console.error('Error loading reporting managers:', error);
        // Set empty array on error to prevent UI issues
        setReportingManagers([]);
        // Optionally show user-friendly error
        setErrors(prev => ({
          ...prev,
          managers: 'Failed to load reporting managers. Please refresh the page.'
        }));
      } finally {
        setLoadingManagers(false);
      }
    };

    loadReportingManagers();
  }, []);

  // FIXED: Simplified handleFieldChange with proper value handling
  const handleFieldChange = (field, value) => {
    // Clear local error for this field when user starts typing
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
    
    // Password validation for new employees
    if (field === 'password' && value && value?.length < 6) {
      setErrors(prev => ({
        ...prev,
        password: 'Password must be at least 6 characters'
      }));
    } else if (field === 'password' && value?.length >= 6) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.password;
        return newErrors;
      });
    }
    
    const updatedData = {
      ...editedData,
      [field]: value
    };
    
    setEditedData(updatedData);
    
    // Real-time sync with parent for add mode
    if (isAddMode && onSave) {
      onSave(updatedData, true);
    }
  };

  // CRITICAL FIX: Enhanced handleSave with validation and logging
  const handleSave = () => {
    console.log('ProfileHeader handleSave called');
    console.log('Current editedData:', editedData);
    
    // CRITICAL: Validate required fields before calling onSave
    if (!editedData?.name?.trim()) {
      alert('Please enter employee name');
      return;
    }
    
    if (!editedData?.email?.trim()) {
      alert('Please enter employee email');
      return;
    }
    
    if (!editedData?.employeeId?.trim()) {
      alert('Please enter employee ID');
      return;
    }
    
    if (!editedData?.position) {
      alert('Please select a position');
      return;
    }
    
    if (!editedData?.department) {
      alert('Please select a department');
      return;
    }
    
    if (!editedData?.roleAssigned) {
      alert('Please select a role');
      return;
    }
    
    // CRITICAL: Ensure all data is properly structured before passing to parent
    const completeData = {
      ...editedData,
      // Ensure required fields are trimmed and properly formatted
      name: editedData?.name?.trim(),
      email: editedData?.email?.toLowerCase()?.trim(),
      employeeId: editedData?.employeeId?.toUpperCase()?.trim(),
      position: editedData?.position,
      department: editedData?.department,
      roleAssigned: editedData?.roleAssigned,
      // Ensure personal details structure exists even if empty
      personalDetails: editedData?.personalDetails || {
        dateOfBirth: '',
        gender: '',
        maritalStatus: '',
        nationality: '',
        religion: '',
        bloodGroup: '',
        address: { street: '', city: '', state: '', zipCode: '' },
        emergencyContact: { name: '', relationship: '', phone: '', email: '' },
        pfNumber: '',
        esiNumber: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        educationQualification: '',
        professionalExperience: ''
      }
    };
    
    console.log('Calling onSave with complete data:', completeData);
    
    // Call parent's onSave handler
    if (onSave) {
      onSave(completeData);
    } else {
      console.error('onSave prop is not defined!');
      alert('Save function is not configured. Please contact support.');
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name?.split(' ')?.map(word => word?.charAt(0))?.join('')?.toUpperCase()?.substring(0, 2);
  };

  // CRITICAL: Add console log to verify disabled state
  console.log('🎯 ProfileHeader render - isEditing:', isEditing, 'disabled will be:', !isEditing);

  return (
    <div className="bg-card rounded-lg border border-border card-shadow mb-6 p-6">
      {/* ENHANCED: More prominent edit mode indicator */}
      {isEditing && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-sm">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full">
              <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            </div>
            <div>
              <p className="text-sm font-bold text-green-800">
                ✏️ EDIT MODE ACTIVE
              </p>
              <p className="text-xs text-green-700 mt-1">
                All fields are now editable. Click on any field to start editing.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        {/* Profile Image Section */}
        <div className="flex-shrink-0">
          <div className="relative">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
              {employee?.profileImage ? (
                <img 
                  src={employee?.profileImage} 
                  alt={employee?.profileImageAlt || `${employee?.name || 'Employee'} profile`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl font-bold text-primary">
                  {getInitials(employee?.name)}
                </span>
              )}
            </div>
            
            {isEditing && (
              <button
                onClick={onImageUpload}
                className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors shadow-lg"
                title="Upload photo"
              >
                <Icon name="Camera" size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Basic Information</h3>
            
            {/* CRITICAL FIX: Use renderKey to force complete re-mount + readOnly instead of disabled */}
            <Input
              key={`name-${renderKey}`}
              label="Full Name"
              value={editedData?.name || ''}
              onChange={(e) => handleFieldChange('name', e?.target?.value)}
              readOnly={!isEditing}
              required
              error={validationErrors?.name || errors?.name}
            />
            
            <Input
              key={`email-${renderKey}`}
              label="Email Address"
              type="email"
              value={editedData?.email || ''}
              onChange={(e) => handleFieldChange('email', e?.target?.value)}
              readOnly={!isEditing}
              required
              error={validationErrors?.email || errors?.email}
            />
            
            <Input
              key={`username-${renderKey}`}
              label="Username"
              value={editedData?.username || ''}
              onChange={(e) => handleFieldChange('username', e?.target?.value)}
              readOnly={!isEditing}
              placeholder="Enter username for login"
              helperText="Username can be used for login instead of email"
            />
            
            {isAddMode && (
              <Input
                key={`password-${renderKey}`}
                label="Password"
                type="password"
                value={editedData?.password || ''}
                onChange={(e) => handleFieldChange('password', e?.target?.value)}
                readOnly={!isEditing}
                required={isAddMode}
                error={errors?.password}
                helperText="Minimum 6 characters required"
              />
            )}
            
            <Input
              key={`phone-${renderKey}`}
              label="Phone Number"
              type="tel"
              value={editedData?.phone || ''}
              onChange={(e) => handleFieldChange('phone', e?.target?.value)}
              readOnly={!isEditing}
            />
          </div>

          {/* Employment Info Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Employment Details</h3>
            
            <Input
              key={`employeeId-${renderKey}`}
              label="Employee ID"
              value={editedData?.employeeId || ''}
              onChange={(e) => handleFieldChange('employeeId', e?.target?.value)}
              readOnly={!isEditing}
              required
              error={validationErrors?.employeeId || errors?.employeeId}
            />
            
            <Select
              key={`roleAssigned-${renderKey}`}
              label="Role Assigned"
              value={editedData?.roleAssigned || ''}
              onValueChange={(value) => handleFieldChange('roleAssigned', value)}
              options={roleOptions}
              disabled={!isEditing}
              placeholder="Select role"
              required
              error={validationErrors?.roleAssigned || errors?.roleAssigned}
            />
            
            <Select
              key={`position-${renderKey}`}
              label="Position"
              value={editedData?.position || ''}
              onValueChange={(value) => handleFieldChange('position', value)}
              options={positionOptions}
              disabled={!isEditing}
              placeholder="Select position"
              searchable
              required
              error={validationErrors?.position || errors?.position}
            />
            
            <Select
              key={`designation-${renderKey}`}
              label="Designation"
              value={editedData?.designation || ''}
              onValueChange={(value) => handleFieldChange('designation', value)}
              options={designationOptions}
              disabled={!isEditing}
              placeholder="Select designation"
              searchable
            />
            
            <Select
              key={`department-${renderKey}`}
              label="Department"
              value={editedData?.department || ''}
              onValueChange={(value) => handleFieldChange('department', value)}
              options={departmentOptions}
              disabled={!isEditing}
              placeholder="Select department"
              searchable
              required
              error={validationErrors?.department || errors?.department}
            />
          </div>
        </div>
      </div>

      {/* Additional Employment Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 pt-6 border-t border-border">
        <Select
          key={`branchName-${renderKey}`}
          label="Branch Name"
          value={editedData?.branchName || ''}
          onValueChange={(value) => handleFieldChange('branchName', value)}
          options={branchOptions}
          disabled={!isEditing}
          placeholder="Select branch"
        />
        
        <Select
          key={`workShift-${renderKey}`}
          label="Work Shift"
          value={editedData?.workShift || ''}
          onValueChange={(value) => handleFieldChange('workShift', value)}
          options={workShiftOptions}
          disabled={!isEditing}
          placeholder="Select work shift"
        />
        
        <Select
          key={`reportingManager-${renderKey}`}
          label="Reporting Manager"
          value={editedData?.reportingManager || ''}
          onValueChange={(value) => handleFieldChange('reportingManager', value)}
          options={reportingManagers}
          disabled={!isEditing || loadingManagers}
          placeholder={loadingManagers ? "Loading managers..." : "Select reporting manager"}
          searchable
          helperText={errors?.managers || "Select the employee's direct manager"}
        />
        
        <Input
          key={`workSchedule-${renderKey}`}
          label="Work Schedule"
          value={editedData?.workSchedule || ''}
          onChange={(e) => handleFieldChange('workSchedule', e?.target?.value)}
          readOnly={!isEditing}
        />
        
        <Input
          key={`salary-${renderKey}`}
          label="Salary"
          type="number"
          value={editedData?.salary || ''}
          onChange={(e) => handleFieldChange('salary', e?.target?.value)}
          readOnly={!isEditing}
        />
        
        <Select
          key={`employmentStatus-${renderKey}`}
          label="Employment Status"
          value={editedData?.employmentStatus || 'active'}
          onValueChange={(value) => handleFieldChange('employmentStatus', value || 'active')}
          options={employmentStatusOptions}
          disabled={!isEditing}
          placeholder="Select employment status"
          error={validationErrors?.employmentStatus}
        />
        
        <Input
          key={`dateOfJoining-${renderKey}`}
          label="Date of Joining"
          type="date"
          value={editedData?.dateOfJoining || ''}
          onChange={(e) => handleFieldChange('dateOfJoining', e?.target?.value)}
          readOnly={!isEditing}
        />
        
        <Input
          key={`location-${renderKey}`}
          label="Location"
          value={editedData?.location || ''}
          onChange={(e) => handleFieldChange('location', e?.target?.value)}
          readOnly={!isEditing}
        />
      </div>

      {/* Action Buttons - Only show if not in add mode */}
      {!isAddMode && (
        <div className="flex items-center space-x-3 mt-6 pt-6 border-t border-border">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onCancel}
                disabled={saving}
              >
                <Icon name="X" size={16} className="mr-2" />
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Icon name="Save" size={16} className="mr-2" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : canEdit ? (
            <Button variant="default" size="sm" onClick={onEdit}>
              <Icon name="Edit" size={16} className="mr-2" />
              Edit Profile
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;